const FIELDS = [
  ["ElementFontFamily", "fontFamily"],
  ["ElementFontSize", "fontSize"],
  ["ElementFontWeight", "fontWeight"],
  ["ElementFontStyle", "fontStyle"],
  ["ElementTextColor", "color"],
  ["ElementTextAlignment", "align"],
  ["ElementLineHeight", "lineHeight"],
  ["ElementLetterSpacing", "letterSpacing"],
  ["ElementTextDecoration", "decoration"],
  ["ElementBackgroundColor", "background"],
];

export const TYPOGRAPHY_KEYS = FIELDS.map(([, key]) => key);

// What a table draws when nothing says otherwise (used by the table model).
export const DEFAULT_TEXT_COLOR = "#1F2A44";
export const DEFAULT_LINE_HEIGHT = 1.5;
export const DEFAULT_LETTER_SPACING = 1;

export function readToolbarStyle(style = {}) {
  const next = {};
  FIELDS.forEach(([from, to]) => {
    const value = style?.[from];
    next[to] = value == null ? "" : String(value);
  });
  return next;
}

export function typographyPatch(previous, next) {
  const patch = {};
  Object.keys(next || {}).forEach((key) => {
    if (next[key] !== previous?.[key]) patch[key] = next[key];
  });
  return patch;
}

export function typographyCssVars(typography) {
  if (!typography) return {};
  const vars = {};
  if (typography.fontFamily) {
    const name = String(typography.fontFamily).replace(/["\\]/g, "");
    vars["--table-font"] = `"${name}", "Lato", sans-serif`;
  }
  const size = Number(String(typography.fontSize ?? "").replace(/px$/i, ""));
  if (size > 0) vars["--table-size"] = `${size}px`;
  if (typography.fontWeight !== undefined && typography.fontWeight !== "") {
    vars["--table-weight"] = String(typography.fontWeight);
    vars["--table-header-weight"] = String(typography.fontWeight);
  }
  if (typography.fontStyle && /italic/i.test(typography.fontStyle)) vars["--table-style"] = "italic";
  else if (typography.fontStyle && /normal/i.test(typography.fontStyle)) vars["--table-style"] = "normal";
  if (typography.color) vars["--table-color"] = typography.color;
  if (typography.align) vars["--table-align"] = String(typography.align).toLowerCase();
  if (typography.lineHeight !== undefined && typography.lineHeight !== "") {
    vars["--table-leading"] = String(typography.lineHeight);
  }
  if (typography.letterSpacing !== undefined && typography.letterSpacing !== "") {
    const spacing = String(typography.letterSpacing);
    vars["--table-tracking"] = /px|em|rem/.test(spacing) ? spacing : `${spacing}px`;
  }
  if (typography.decoration !== undefined) vars["--table-decoration"] = typography.decoration || "none";
  if (typography.background) vars["--table-bg"] = typography.background;
  return vars;
}

const SIZE_PX = { s: 12, m: 14, l: 17 };

// The toolbar reads Styles[0]. A new table's Styles[0] would otherwise carry the generic element defaults
// (Lato / 30px / bold), so the toolbar would show values the table does not draw. Seed it from the table.
export function toolbarStyleForModel(model) {
  const typo = model?.typography || {};
  const size = Number(String(typo.fontSize ?? "").replace(/px$/i, "")) || SIZE_PX[model?.size] || SIZE_PX.m;
  return {
    ElementFontFamily: typo.fontFamily || "DM Sans",
    ElementFontSize: size,
    ElementFontWeight: Number(typo.fontWeight) || 400,
    ElementFontStyle: /italic/i.test(typo.fontStyle || "") ? "italic" : "Normal",
  };
}

const FIELD_BY_KEY = Object.fromEntries(FIELDS.map(([from, to]) => [to, from]));

// The toolbar style for a typography object. The toolbar reads it from the table's saved style (Styles[0]), so it
// holds only real values:
// - font family, size and alignment are always there: they are what the table draws, and the toolbar needs them;
// - every other field is there only when the table (or the cell) sets it. Otherwise it is `undefined`, which clears
//   whatever the previous selection left in Styles[0] and is dropped when the design is stored, so no made-up
//   default is saved. The toolbar shows its own defaults for a field that is not there.
export function typographyToToolbarStyle(model, typography) {
  const base = toolbarStyleForModel(model);
  const typo = typography || {};
  const style = {
    ElementFontFamily: base.ElementFontFamily,
    ElementFontSize: base.ElementFontSize,
    ElementTextAlignment: model?.align || "left",
    ElementFontWeight: undefined,
    ElementFontStyle: undefined,
    ElementTextColor: undefined,
    ElementLineHeight: undefined,
    ElementLetterSpacing: undefined,
    ElementTextDecoration: undefined,
    ElementBackgroundColor: undefined,
  };
  Object.keys(typo).forEach((key) => {
    const field = FIELD_BY_KEY[key];
    if (field && typo[key] !== "" && typo[key] != null) style[field] = typo[key];
  });
  return style;
}
